import { router } from 'expo-router';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { OtpInput } from 'react-native-otp-entry';

import { Button, IconInfo, IconMail, IconRefresh, Pressable, Text, tokens } from '@fhs/ui';
import { formatPhoneNumber } from '@fhs/ui/src/components/form-text-inputs/masks';

import { IdentifyActionSheet } from '../components/actions-sheets/identify-action-sheet';
import {
  useCreateOtp,
  useJwtIdentifier,
  useNavigateWithJwt,
  useResendOtp,
  useValidateOtp,
} from '../hooks';
import { AuthIdentifiers, IdentifyAuthJwt } from '../types';

const OTP_LENGTH = 6;

export function VerifyOtp() {
  const [showIdentifyPopup, setShowIdentifyPopup] = useState<boolean>(false);
  const { createOtp, otpLoading, otpError } = useCreateOtp();
  const onIdentifyChallenge = useCallback(() => setShowIdentifyPopup(true), [setShowIdentifyPopup]);
  const {
    identifyAuthJwts,
    validatingOtp,
    validateOtp,
    otpValidationError,
    setOtpValidationError,
  } = useValidateOtp({
    onIdentifyChallenge,
  });
  const { resendOtp, resendOtpLoading, resendCountdownMessage } = useResendOtp();
  const { getJwtIdentifier } = useJwtIdentifier();
  const { navigateWithJWT } = useNavigateWithJwt();

  useEffect(() => {
    if (otpError) {
      setShowIdentifyPopup(false);
      router.dismissTo('/v2/signin');
    }
  }, [otpError]);

  const attemptValidateCode = useCallback(
    async otpInput => {
      await validateOtp(otpInput);
    },
    [validateOtp]
  );

  const getIdentifierComponent = useCallback((loginIdentifier, identifierType) => {
    switch (identifierType) {
      case AuthIdentifiers.PHONE_NUMBER:
        //Format as phone
        return <Text weight="bold">{formatPhoneNumber(loginIdentifier)} </Text>;
      case AuthIdentifiers.EMAIL:
        return <Text weight="bold">{loginIdentifier} </Text>;
      case AuthIdentifiers.JWT:
        return (
          <Text weight="bold">
            {jwtDecode<IdentifyAuthJwt>(loginIdentifier).redactedIdentity.redactedEmail}
          </Text>
        );
    }
  }, []);

  const getLinkComponent = useCallback(identifierType => {
    switch (identifierType) {
      case AuthIdentifiers.PHONE_NUMBER:
        return (
          <Pressable onPress={() => router.dismissTo('/v2/signin')}>
            <Text.Paragraph size="md" style={{ textDecorationLine: 'underline' }}>
              Change Number
            </Text.Paragraph>
          </Pressable>
        );
      case AuthIdentifiers.EMAIL:
      case AuthIdentifiers.JWT:
        return (
          <Pressable onPress={() => router.replace('/v2/signin/continuewithemail')}>
            <Text.Paragraph size="md" style={{ textDecorationLine: 'underline' }}>
              Change Email
            </Text.Paragraph>
          </Pressable>
        );
    }
  }, []);

  const { loginIdentifier, identifierType } = getJwtIdentifier();
  const resendOtpDisabledMemo = useMemo(
    () => validatingOtp || otpLoading || resendCountdownMessage !== '' || resendOtpLoading,
    [otpLoading, resendCountdownMessage, resendOtpLoading, validatingOtp]
  );
  const verifyWithEmailDisabledMemo = useMemo(
    () => validatingOtp || otpLoading || resendOtpLoading,
    [otpLoading, resendOtpLoading, validatingOtp]
  );
  return (
    <>
      <View style={{ gap: 12 }}>
        <Text.Heading type={'three'}>Verify With Code </Text.Heading>
        <Text>
          {'Enter the 6-digit code sent to you at '}
          {getIdentifierComponent(loginIdentifier, identifierType)}
        </Text>
        {getLinkComponent(identifierType)}
      </View>
      <View style={styles.otpContainerView}>
        <OtpInput
          numberOfDigits={OTP_LENGTH}
          focusColor={tokens.colors.$blackOpacity30}
          onTextChange={() => {
            setOtpValidationError(false);
          }}
          autoFocus
          blurOnFilled
          disabled={validatingOtp}
          type="numeric"
          onFilled={attemptValidateCode}
          theme={{
            disabledPinCodeContainerStyle: styles.otpContainerDisable,
            containerStyle: styles.otpContainer,
            pinCodeContainerStyle: styles.inputBox,
            filledPinCodeContainerStyle: otpValidationError
              ? styles.erroredInputBox
              : styles.inputBox,
          }}
          textInputProps={{
            accessibilityLabel: 'One-Time Password',
          }}
          textProps={{
            accessibilityRole: 'text',
            accessibilityLabel: 'OTP digit',
            allowFontScaling: false,
            style: styles.otpText,
          }}
        />
        {otpValidationError && (
          <Text style={styles.errorMessage}>The code you've entered is incorrect.</Text>
        )}
      </View>

      <View style={styles.buttonContainer}>
        <View style={{ flexDirection: 'row', gap: 24 }}>
          <Button
            style={styles.button}
            type={'outline'}
            size="sm"
            onPress={resendOtp}
            disabled={resendOtpDisabledMemo}
            loading={resendOtpLoading}
          >
            {!resendOtpLoading && (
              <Button.Icon>
                <IconRefresh style={styles.buttonContent} />
              </Button.Icon>
            )}
            <Button.Text
              style={[styles.buttonContent, resendOtpDisabledMemo && styles.buttonContentDisabled]}
            >
              Resend Code
            </Button.Text>
          </Button>
          {validatingOtp && <ActivityIndicator color={tokens.colors.$houseRedDarken} />}
        </View>
        {resendCountdownMessage !== '' && <Text>{resendCountdownMessage}</Text>}
        <Button
          style={styles.button}
          type={'outline'}
          size="sm"
          onPress={() => {
            if (identifierType === AuthIdentifiers.JWT) {
              navigateWithJWT('/v2/signin/continuewithemail');
            } else {
              router.replace('/v2/signin/continuewithemail');
            }
          }}
          disabled={verifyWithEmailDisabledMemo}
        >
          {!resendOtpLoading && (
            <Button.Icon>
              {identifierType !== AuthIdentifiers.PHONE_NUMBER ? (
                <IconInfo style={styles.buttonContent} />
              ) : (
                <IconMail style={styles.buttonContent} />
              )}
            </Button.Icon>
          )}
          <Button.Text
            style={[
              styles.buttonContent,
              verifyWithEmailDisabledMemo && styles.buttonContentDisabled,
            ]}
          >
            {identifierType !== AuthIdentifiers.PHONE_NUMBER
              ? 'Not Your Email'
              : 'Verify With Email'}
          </Button.Text>
        </Button>
      </View>
      {showIdentifyPopup && (
        <IdentifyActionSheet
          authJwts={identifyAuthJwts}
          isVisible
          onAccountSelect={() => {
            setShowIdentifyPopup(false);
          }}
          onSelectNoneOfAbove={() => {
            navigateWithJWT('/v2/signin/continuewithemail');
          }}
          onSelect={(jwt: string) => {
            createOtp({
              loginIdentifier: jwt,
              identifierType: AuthIdentifiers.JWT,
            });
          }}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    borderColor: tokens.colors.$blackOpacity75,
    paddingHorizontal: 16,
  },
  buttonContainer: {
    marginRight: 'auto',
    alignItems: 'flex-start',
    gap: 16,
  },
  buttonContent: {
    alignItems: 'center',
    color: tokens.colors.$black,
  },
  buttonContentDisabled: {
    color: tokens.colors.$disabledText,
  },
  inputBox: {
    width: 48,
    height: 48,
    textAlign: 'center',
    color: tokens.colors.$black,
    borderColor: tokens.colors.$blackOpacity30,
    borderWidth: 1,
    borderRadius: 4,
  },
  erroredInputBox: {
    borderColor: tokens.colors.$error,
  },
  errorMessage: {
    color: tokens.colors.$houseRed,
    fontSize: 12,
  },
  otpContainerView: {
    gap: 8,
    width: '100%',
    marginVertical: 28,
  },
  otpContainer: {
    width: 'auto',
    alignContent: 'center',
  },
  otpContainerDisable: {
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  otpText: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 400,
  },
});
